/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import { logger } from "@/utils/logger";
import { motion } from "framer-motion";
import { Home, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { savePlayerNames, loadPlayerNames } from "@/utils/playerStorage";
import { recordGameWon } from "@/utils/playerProfile";

import { getVoiceLang } from "@/utils/voicePref";
const CARS = [
  { emoji: "🚗", name: "Mobil Merah", color: "#FF6347" },
  { emoji: "🚙", name: "Mobil Biru", color: "#1E90FF" },
  { emoji: "🏎️", name: "Mobil Balap", color: "#FFD700" },
  { emoji: "🚕", name: "Taksi", color: "#FFD700" },
];

const TRACKS = [
  { id: 1, name: "Lurus", obstacles: [], laps: 1, length: 100 },
  { id: 2, name: "Rintangan", obstacles: [30, 60], laps: 1, length: 100 },
  { id: 3, name: "Panjang", obstacles: [20, 45, 70], laps: 1, length: 150 },
];

const speakText = (t) => {
  try { if (!window.speechSynthesis) return; window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t); u.lang = getVoiceLang(); u.rate=0.85; u.pitch=1.0; window.speechSynthesis.speak(u);
  } catch (err) { logger.debug("game op failed", err); }
};

const GameBalap = ({ onBackToMenu }) => {
  const { width: winW, height: winH } = useWindowSize();
  const [gameState, setGameState] = useState("menu");
  const [playerNames, setPlayerNames] = useState(() => { const s = loadPlayerNames(); return [s[0]||"",s[1]||""]; });
  const [playerCount, setPlayerCount] = useState(2);
  const [carChoices, setCarChoices] = useState([0, 1, 2, 3]);
  const [track, setTrack] = useState(0);
  const [positions, setPositions] = useState([0, 0, 0, 0]);
  const [speeds, setSpeeds] = useState([0, 0, 0, 0]);
  const [winner, setWinner] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [countdown, setCountdown] = useState(0);
  const rafRef = useRef(null);

  const names = playerNames.map((n, i) => n || `Pemain ${i + 1}`);
  const currentTrack = TRACKS[track];
  const keys = ['a', 'j', 'f', 'k'];

  const startGame = () => {
    savePlayerNames(playerNames);
    setPositions([0, 0, 0, 0]);
    setSpeeds([0, 0, 0, 0]);
    setWinner(null);
    setCountdown(3);
    setGameState("countdown");
  };

  useEffect(() => {
    if (gameState !== "countdown") return;
    if (countdown <= 0) { setGameState("playing"); if (soundOn) speakText("Start!"); return; }
    const t = setTimeout(() => { setCountdown(prev => prev - 1); if (soundOn) speakText(String(countdown)); }, 1000);
    return () => clearTimeout(t);
  }, [gameState, countdown, soundOn]);

  // Physics loop
  useEffect(() => {
    if (gameState !== "playing") return;
    const loop = () => {
      setPositions(prev => {
        const next = [...prev];
        for (let i = 0; i < playerCount; i++) {
          next[i] += speeds[i];
          // Friction
          setSpeeds(s => { const n = [...s]; n[i] *= 0.96; return n; });
          // Obstacle slowdown
          currentTrack.obstacles.forEach(obs => {
            if (Math.abs(next[i] - obs) < 3) {
              setSpeeds(s => { const n = [...s]; n[i] *= 0.7; return n; });
            }
          });
          if (next[i] >= currentTrack.length && !winner) {
            setWinner(i);
            recordGameWon("balap");
            if (soundOn) speakText(`${names[i]} wins! ${CARS[carChoices[i]].name} is fastest!`);
          }
        }
        return next;
      });
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [gameState, speeds, winner, playerCount, currentTrack, names, carChoices, soundOn]);

  // Keyboard
  useEffect(() => {
    if (gameState !== "playing") return;
    const handle = (e) => {
      const idx = keys.indexOf(e.key.toLowerCase());
      if (idx >= 0 && idx < playerCount) {
        setSpeeds(prev => { const n = [...prev]; n[idx] = Math.min(n[idx] + 0.8, 3); return n; });
      }
    };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, [gameState, playerCount]);

  const tapAccel = (idx) => {
    if (gameState !== "playing" || winner) return;
    setSpeeds(prev => { const n = [...prev]; n[idx] = Math.min(n[idx] + 1, 3.5); return n; });
  };

  if (gameState === "menu") {
    return (
      <div className="flex flex-col items-center w-full max-w-2xl mx-auto px-4 pt-4" data-testid="game-balap-menu">
        <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="glass-card-strong rounded-2xl p-6 w-full text-center mb-4">
          <motion.span className="text-6xl block mb-3" animate={{ x: [0, 10, 0] }} transition={{ duration: 1, repeat: Infinity }}>🏎️</motion.span>
          <h1 className="font-fredoka text-3xl text-gold font-bold mb-2">Game Balap</h1>
          <p className="font-fredoka text-gold-light/80 text-sm mb-3">Tap cepat untuk akselerasi!</p>
          <div className="flex gap-2 mb-3 justify-center">
            {[2, 3, 4].map(n => (
              <Button key={n} data-testid={`balap-pc-${n}`} onClick={() => setPlayerCount(n)} className={`font-fredoka text-xs rounded-lg ${playerCount === n ? 'bg-gold text-brown-dark' : 'glass-card text-gold'}`}>{n}P</Button>
            ))}
          </div>
          <div className="flex gap-2 mb-3 justify-center">
            {TRACKS.map((t, i) => (
              <Button key={t.id} data-testid={`balap-track-${i}`} onClick={() => setTrack(i)} className={`font-fredoka text-xs rounded-lg ${track === i ? 'bg-gold text-brown-dark' : 'glass-card text-gold'}`}>{t.name}</Button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {Array.from({ length: playerCount }).map((_, i) => (
              <div key={i} className="flex items-center gap-1">
                <select value={carChoices[i]} onChange={e => { const n = [...carChoices]; n[i] = parseInt(e.target.value); setCarChoices(n); }}
                  className="glass-card border-gold/20 rounded px-1 py-0.5 text-white font-fredoka text-xs bg-transparent w-12">
                  {CARS.map((c, ci) => <option key={ci} value={ci}>{c.emoji}</option>)}
                </select>
                <input type="text" value={playerNames[i]} onChange={e => { const n = [...playerNames]; n[i] = e.target.value; setPlayerNames(n); }}
                  placeholder={`P${i+1}`} maxLength={10} className="flex-1 glass-card border-gold/20 rounded-xl px-2 py-1 text-white font-fredoka text-xs focus:outline-none" />
              </div>
            ))}
          </div>
          <Button onClick={startGame} className="w-full h-12 font-fredoka font-bold text-lg rounded-xl bg-gradient-to-r from-red-500 to-orange-500" data-testid="balap-start">MULAI BALAP!</Button>
        </motion.div>
        <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold hover:bg-gold/10 font-fredoka rounded-xl"><Home className="w-4 h-4 mr-2" />Menu</Button>
      </div>
    );
  }

  // Countdown
  if (gameState === "countdown") {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <motion.span key={countdown} initial={{ scale: 3, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0 }}
          className="font-fredoka text-8xl text-gold font-bold">{countdown || "GO!"}</motion.span>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center w-full max-w-3xl mx-auto px-2" data-testid="game-balap-game">
      {winner !== null && <Confetti width={winW} height={winH} recycle={false} numberOfPieces={300} />}
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <span className="font-fredoka text-gold font-bold text-sm">🏁 {currentTrack.name}</span>
        <div className="flex gap-1.5">
          <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 w-7 p-0">{soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}</Button>
          <Button onClick={() => setGameState("menu")} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Home className="w-3 h-3" /></Button>
        </div>
      </div>

      {/* Race tracks */}
      <div className="w-full rounded-2xl overflow-hidden p-3" style={{ background: 'linear-gradient(180deg, #2d2d2d 0%, #1a1a1a 100%)' }}>
        {Array.from({ length: playerCount }).map((_, i) => (
          <div key={i} className="relative mb-2 last:mb-0">
            <div className="h-12 sm:h-14 rounded-lg relative overflow-hidden" style={{ background: '#333', border: `2px solid ${CARS[carChoices[i]].color}40` }}>
              {/* Road lines */}
              <div className="absolute inset-y-0 left-0 right-0 flex items-center">
                <div className="w-full h-0.5 border-t-2 border-dashed border-white/20" />
              </div>
              {/* Obstacles */}
              {currentTrack.obstacles.map((obs, oi) => (
                <div key={oi} className="absolute top-1 bottom-1 w-3 bg-yellow-500/30 rounded" style={{ left: `${(obs / currentTrack.length) * 100}%` }}>
                  <span className="text-xs">⚠️</span>
                </div>
              ))}
              {/* Finish */}
              <div className="absolute right-1 top-0 bottom-0 w-1.5" style={{ backgroundImage: 'repeating-linear-gradient(0deg, #fff 0px, #fff 4px, #000 4px, #000 8px)' }} />
              {/* Car */}
              <motion.div className="absolute top-1/2 -translate-y-1/2 text-2xl sm:text-3xl z-10"
                style={{ left: `${Math.min((positions[i] / currentTrack.length) * 95, 95)}%` }}>
                {CARS[carChoices[i]].emoji}
              </motion.div>
              <span className="absolute left-2 top-0.5 font-fredoka text-[10px] text-white/50">{names[i]} ({keys[i].toUpperCase()})</span>
              {winner === i && <span className="absolute right-6 top-1/2 -translate-y-1/2 text-xl">🏆</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Winner */}
      {winner !== null && (
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="mt-3 glass-card-strong rounded-2xl p-4 text-center w-full max-w-md">
          <span className="text-4xl">{CARS[carChoices[winner]].emoji}</span>
          <h2 className="font-fredoka text-2xl text-gold font-bold">{names[winner]} Menang!</h2>
          <div className="flex gap-2 mt-2">
            <Button onClick={startGame} className="flex-1 font-fredoka font-bold rounded-xl bg-gradient-to-r from-yellow-400 to-amber-500"><RotateCcw className="w-4 h-4 mr-2" />Lagi</Button>
            <Button onClick={() => setGameState("menu")} variant="outline" className="flex-1 glass-card border-gold/20 text-gold font-fredoka rounded-xl">Menu</Button>
          </div>
        </motion.div>
      )}

      {/* Tap buttons */}
      {!winner && gameState === "playing" && (
        <div className={`grid gap-2 w-full mt-3 ${playerCount <= 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-4'}`}>
          {Array.from({ length: playerCount }).map((_, i) => (
            <Button key={i} data-testid={`balap-tap-${i}`} onClick={() => tapAccel(i)}
              className="h-16 font-fredoka font-bold text-sm rounded-2xl btn-3d"
              style={{ background: CARS[carChoices[i]].color, touchAction: "manipulation" }}>
              {CARS[carChoices[i]].emoji} {names[i]}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export default GameBalap;
